import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
  Heart, Shield, DollarSign, Users, ArrowRight, X, Loader2,
  CheckCircle2, AlertCircle, Building2, ExternalLink
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PRESET_AMOUNTS = [25, 50, 100, 250, 500, 1000];

const DonationForm = ({ charity, campaign, businessId, donorType, onSuccess, onClose, matchInfo }) => {
  const [amount, setAmount] = useState(50);
  const [customAmount, setCustomAmount] = useState('');
  const [donorName, setDonorName] = useState('');
  const [donorEmail, setDonorEmail] = useState('');
  const [starting, setStarting] = useState(false);
  const [frequency, setFrequency] = useState('ONCE');
  const [charityStatus, setCharityStatus] = useState(null);
  const [error, setError] = useState('');
  const [step, setStep] = useState('form'); // form | waiting | success
  const [donationId, setDonationId] = useState(null);
  const [donateUrl, setDonateUrl] = useState('');
  const pollRef = useRef(null);

  const effectiveAmount = customAmount ? parseFloat(customAmount) : amount;
  const matchRatio = matchInfo?.match_ratio || campaign?.employer_match_ratio || 0;
  const matchEnabled = donorType === 'employee' && (matchInfo?.match_enabled || campaign?.employer_match_enabled);
  const matchAmount = matchEnabled ? Math.min(effectiveAmount * matchRatio, matchInfo?.match_cap_per_employee || 500) : 0;
  const totalImpact = effectiveAmount + matchAmount;

  useEffect(() => () => clearInterval(pollRef.current), []);

  useEffect(() => {
    const ein = (charity.ein || charity.charity_ein || '').replace(/-/g, '');
    if (!ein) return;
    axios.get(`${BACKEND_URL}/api/everyorg/nonprofit/${ein}/status`)
      .then(r => setCharityStatus(r.data))
      .catch(() => {});
  }, [charity]);

  const pollForConfirmation = (id) => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/donations/track/${id}`);
        if (data.payment_status === 'paid') {
          clearInterval(pollRef.current);
          setStep('success');
          if (onSuccess) onSuccess({ donation_id: id, amount: data.amount, match_amount: matchAmount });
        }
      } catch (e) {
        // keep polling; the donor may still be on the Every.org checkout
      }
    }, 4000);
  };

  const startDonation = async (e) => {
    e.preventDefault();
    setError('');
    if (!effectiveAmount || effectiveAmount < 1) { setError('Please enter an amount of $1 or more'); return; }

    setStarting(true);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/everyorg/donations`, {
        amount: effectiveAmount,
        charity_ein: charity.ein || charity.charity_ein,
        charity_name: charity.name || charity.charity_name,
        campaign_id: campaign?.campaign_id,
        business_id: businessId,
        donor_name: donorName,
        donor_email: donorEmail,
        donor_type: donorType,
        employer_match_amount: matchAmount,
        frequency,
        return_origin: window.location.origin,
      });
      setDonationId(data.donation_id);
      setDonateUrl(data.donate_url);
      window.open(data.donate_url, '_blank', 'noopener');
      setStep('waiting');
      pollForConfirmation(data.donation_id);
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not start the donation. Please try again.');
    } finally {
      setStarting(false);
    }
  };

  if (step === 'success') {
    return (
      <div className="text-center py-8 px-2" data-testid="donation-success">
        <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center mb-5 shadow-lg shadow-blue-200">
          <CheckCircle2 className="w-10 h-10 text-white" />
        </div>
        <h3 className="text-2xl font-bold text-gray-900 mb-2">Donation confirmed</h3>
        <p className="text-gray-600">
          <span className="font-bold text-blue-600">${effectiveAmount.toLocaleString()}</span> to {charity.name || charity.charity_name}
        </p>
        {matchAmount > 0 && (
          <p className="text-sm text-purple-600 font-semibold mt-1">
            + ${matchAmount.toLocaleString()} employer match = ${totalImpact.toLocaleString()} total impact
          </p>
        )}
        <div className="mt-6 space-y-3 text-left">
          <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-xl">
            <Shield className="w-4 h-4 text-blue-600 flex-shrink-0" />
            <p className="text-xs text-blue-800">
              <strong>Received by Every.org</strong> — your tax receipt is issued by Every.org, a 501(c)(3)
            </p>
            <CheckCircle2 className="w-5 h-5 text-blue-500 ml-auto flex-shrink-0" />
          </div>
          <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-xl">
            <ArrowRight className="w-4 h-4 text-amber-600 flex-shrink-0" />
            <p className="text-xs text-amber-800">
              <strong>Grant to the charity</strong> — Every.org disburses to {charity.name || 'the charity'} on their next cycle
            </p>
          </div>
        </div>
        <div className="mt-6 flex gap-2">
          <a href={`/receipt/${donationId}`} target="_blank" rel="noreferrer"
            className="flex-1 py-3 border-2 border-gray-200 text-gray-700 rounded-xl font-semibold text-sm hover:border-gray-300 text-center"
            data-testid="view-receipt-link">
            View receipt
          </a>
          <button onClick={onClose} data-testid="donation-done-btn"
            className="flex-1 py-3 bg-gray-900 text-white rounded-xl font-semibold text-sm hover:bg-gray-800">
            Done
          </button>
        </div>
      </div>
    );
  }

  if (step === 'waiting') {
    return (
      <div className="text-center py-8 px-2" data-testid="donation-waiting">
        <Loader2 className="w-10 h-10 text-blue-600 animate-spin mx-auto mb-5" />
        <h3 className="text-xl font-bold text-gray-900 mb-2">Finish in the Every.org tab</h3>
        <p className="text-sm text-gray-500 leading-relaxed">
          We opened Every.org's secure checkout in a new tab. Complete your ${effectiveAmount.toLocaleString()} gift there —
          this page updates automatically as soon as it's confirmed.
        </p>
        <p className="mt-4 text-xs text-gray-400">
          Tip: Every.org lets you cover the processing fee so 100% of your gift reaches the charity, and bank transfers are fee-free.
        </p>
        <a href={donateUrl} target="_blank" rel="noreferrer"
          className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-blue-600 hover:text-blue-700"
          data-testid="reopen-checkout-link">
          <ExternalLink className="w-4 h-4" /> Reopen checkout
        </a>
        <button onClick={onClose} className="mt-6 w-full py-3 border-2 border-gray-200 text-gray-600 rounded-xl font-semibold text-sm hover:border-gray-300">
          Close
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={startDonation} className="space-y-5">
      <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl p-4 text-white">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center">
            <Heart className="w-5 h-5" />
          </div>
          <div>
            <p className="font-bold text-sm">{charity.name || charity.charity_name}</p>
            <p className="text-xs text-slate-400">{charity.city}{charity.state ? `, ${charity.state}` : ''}</p>
          </div>
          {charity.irs_verified !== false && (
            <span className="ml-auto px-2 py-1 bg-blue-500/20 text-blue-300 text-[10px] font-bold rounded-full flex items-center gap-1">
              <Shield className="w-3 h-3" /> Verified 501(c)(3)
            </span>
          )}
        </div>
      </div>

      <div>
        <label className="block text-sm font-bold text-gray-800 mb-2">Donation Amount</label>
        <div className="flex gap-2 mb-3" data-testid="frequency-toggle">
          {[
            { value: 'ONCE', label: 'One-time' },
            { value: 'MONTHLY', label: 'Monthly' },
          ].map(opt => (
            <button key={opt.value} type="button" onClick={() => setFrequency(opt.value)}
              data-testid={`frequency-${opt.value.toLowerCase()}`}
              className={`flex-1 py-2 rounded-xl text-xs font-bold border-2 transition-all ${
                frequency === opt.value
                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                  : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
              }`}>
              {opt.label}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-3 gap-2 mb-3">
          {PRESET_AMOUNTS.map(a => (
            <button key={a} type="button" data-testid={`amount-${a}`}
              onClick={() => { setAmount(a); setCustomAmount(''); }}
              className={`py-2.5 rounded-xl text-sm font-bold transition-all border-2 ${
                !customAmount && amount === a
                  ? 'border-blue-500 bg-blue-50 text-blue-700 shadow-sm'
                  : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
              }`}>
              ${a.toLocaleString()}
            </button>
          ))}
        </div>
        <div className="relative">
          <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input type="number" placeholder="Custom amount" value={customAmount}
            onChange={e => { setCustomAmount(e.target.value); setError(''); }}
            data-testid="custom-amount-input"
            className="w-full pl-9 pr-4 py-3 border-2 border-gray-200 rounded-xl text-sm font-semibold focus:border-blue-500 focus:ring-0 transition-colors" />
        </div>
      </div>

      {matchEnabled && effectiveAmount > 0 && (
        <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-xl p-4" data-testid="match-banner">
          <div className="flex items-center gap-2 mb-2">
            <Users className="w-4 h-4 text-purple-600" />
            <span className="text-xs font-bold text-purple-800">Employer Match: {matchRatio}:1</span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600">Your donation</span>
            <span className="font-bold">${effectiveAmount.toLocaleString()}</span>
          </div>
          <div className="flex items-center justify-between text-sm text-purple-700">
            <span>Employer match</span>
            <span className="font-bold">+ ${matchAmount.toLocaleString()}</span>
          </div>
          <div className="border-t border-purple-200 mt-2 pt-2 flex items-center justify-between">
            <span className="text-sm font-bold text-gray-900">Total Impact</span>
            <span className="text-lg font-black text-purple-700">${totalImpact.toLocaleString()}</span>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">Full Name</label>
          <input type="text" value={donorName} onChange={e => setDonorName(e.target.value)}
            placeholder="Jane Smith" data-testid="donor-name-input"
            className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl text-sm focus:border-blue-500 focus:ring-0" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">Email</label>
          <input type="email" value={donorEmail} onChange={e => setDonorEmail(e.target.value)}
            placeholder="jane@company.com" data-testid="donor-email-input"
            className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl text-sm focus:border-blue-500 focus:ring-0" />
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700" data-testid="donation-error">
          <AlertCircle className="w-4 h-4 flex-shrink-0" /> {error}
        </div>
      )}

      <div className="bg-gray-50 rounded-xl p-3">
        <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-2">
          How your donation reaches {charity.name || charity.charity_name}
        </p>
        <div className="flex items-center gap-2 text-xs text-gray-600">
          <span className="px-2 py-1 bg-white rounded-lg font-semibold border border-gray-200">You</span>
          <ArrowRight className="w-3 h-3 text-gray-400" />
          <span className="px-2 py-1 bg-blue-50 rounded-lg font-semibold border border-blue-200 text-blue-700 flex items-center gap-1">
            <Shield className="w-3 h-3" /> Every.org
          </span>
          <ArrowRight className="w-3 h-3 text-gray-400" />
          <span className="px-2 py-1 bg-indigo-50 rounded-lg font-semibold border border-indigo-200 text-indigo-700 flex items-center gap-1">
            <Building2 className="w-3 h-3" />
            {(charity.name || charity.charity_name || '').length > 15
              ? (charity.name || charity.charity_name).slice(0, 15) + '…'
              : (charity.name || charity.charity_name)}
          </span>
        </div>
        <p className="mt-2 text-[11px] text-gray-500 leading-relaxed">
          Every.org is a 501(c)(3) that charges the card, issues your tax receipt and grants the money to the charity.
          Altrunic charges <strong>0% platform fee</strong>; only card processing (2.2% + $0.30) applies, and you can choose to cover it.
        </p>
      </div>

      {charityStatus && charityStatus.found && !charityStatus.claimed && (
        <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-xl" data-testid="charity-unclaimed-notice">
          <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-[11px] text-amber-800 leading-relaxed">
            <strong>{charityStatus.name || 'This charity'} hasn't claimed its Every.org profile yet.</strong> Your gift is still
            safe and tax-deductible, but Every.org disburses by {charityStatus.disbursement_type || 'grant/check'}, so it can
            take longer to reach them.
          </p>
        </div>
      )}

      <button type="submit" disabled={starting} data-testid="donate-submit-btn"
        className="w-full py-3.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-bold text-sm hover:from-blue-700 hover:to-indigo-700 transition-all disabled:opacity-50 flex items-center justify-center gap-2 shadow-lg shadow-blue-200">
        {starting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Shield className="w-4 h-4" />}
        {starting ? 'Preparing checkout…' : `Continue to secure checkout · $${effectiveAmount > 0 ? effectiveAmount.toLocaleString() : '0'}${frequency === 'MONTHLY' ? '/mo' : ''}`}
      </button>

      <p className="text-center text-[10px] text-gray-400 flex items-center justify-center gap-1">
        <Shield className="w-3 h-3" /> Payment and tax receipt handled by Every.org
      </p>
    </form>
  );
};

const DonationModal = ({ isOpen, onClose, charity, campaign, businessId, donorType = 'guest', matchInfo, onSuccess }) => {
  if (!isOpen || !charity) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center" style={{ zIndex: 10001 }} data-testid="donation-modal">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onMouseDown={onClose} />
      <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b bg-gradient-to-r from-gray-50 to-blue-50">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
              <Heart className="w-4 h-4 text-white" />
            </div>
            <div>
              <h2 className="text-sm font-bold text-gray-900">Make a Donation</h2>
              <p className="text-[11px] text-gray-500">0% platform fee — processed by Every.org</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors" data-testid="donation-close-btn">
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        <div className="p-6">
          <DonationForm
            charity={charity}
            campaign={campaign}
            businessId={businessId}
            donorType={donorType}
            matchInfo={matchInfo}
            onSuccess={onSuccess}
            onClose={onClose}
          />
        </div>
      </div>
    </div>
  );
};

export default DonationModal;
